import { useSessionValue } from '@portal/appLayer/auth';
import { LocaleEnum } from '@portal/schema/locale';
import { AU_NZ_STORES, StoreEnum } from '@portal/schema/storeCode';
import { useCustomerDetails } from '@portal/shared/components/SiteCart/hooks/useCustomerDetails';
import {
  ANZ_COLLECTION,
  EU_DE_COLLECTION,
  EU_EN_COLLECTION,
  MEMBER_PORTAL_APAC_COLLECTION,
  MEMBER_PORTAL_COLLECTION,
  MEMBER_PORTAL_COLLECTION_V53
} from '@portal/shared/constants/shopify';
import { useLocale } from '@portal/shared/hooks/useLocale';
import usePageId from '@portal/shared/hooks/usePageId';
import { useMemo } from 'react';
import { useQueryGetSubscriptionDetailsV2 } from '../internal/getSubscriptionDetails';
import { useQueryGetSubscriptionsV2 } from '../internal/getSubscriptions';
import { useStoreValue } from './useStore';
import { useV53AccountIdentifier } from './useV53AccountIdentifier';

const DE_LOCALES = [LocaleEnum.deEU, LocaleEnum.deEud];
const DACH_COUNTRIES = ['DE', 'AT', 'CH'];
const US_CA_COUNTRIES = ['US', 'CA'];

// Helper hook to determine the appropriate collection handle based on global standards
// To be used across collection areas, e.g Member Shop, subscription addons, cart upsell, etc.
export function useCollectionHandle() {
  const locale = useLocale();
  const store = useStoreValue();
  const v53Enabled = useV53AccountIdentifier();

  // Check selected subscription data for specific shipping address country code
  const id = usePageId();
  const { data: subscriptionData } = useQueryGetSubscriptionDetailsV2(id);
  const selectedSubscriptionCountryCode = subscriptionData?.shipping_address?.country_code || '';
  const isSubPage = !!subscriptionData;

  // Check closest active sub for fallback shipping country code
  const { data: subscriptions = [] } = useQueryGetSubscriptionsV2();
  const closestActiveSub = useMemo(
    () =>
      subscriptions
        ?.filter((i) => i.is_active)
        .sort((a, b) => a.next_order_date - b.next_order_date)[0],
    [subscriptions]
  );
  const closestActiveSubCountryCode = closestActiveSub?.shipping_address?.country_code || '';

  // Check customer default address for fallback shipping country code
  const session = useSessionValue();
  const customerAccessToken = useMemo(
    () => session?.accessTokenPayload.shopify?.accessToken,
    [session?.accessTokenPayload.shopify?.accessToken]
  );
  const { customer } = useCustomerDetails(store, customerAccessToken);
  const customerDefaultAddressCountryCode = customer?.defaultAddress?.countryCodeV2 || '';

  const selectedCountryCode =
    selectedSubscriptionCountryCode ||
    closestActiveSubCountryCode ||
    customerDefaultAddressCountryCode;
  const isDACHShipping = DACH_COUNTRIES.includes(selectedCountryCode);
  const isUSCAShipping = US_CA_COUNTRIES.includes(selectedCountryCode);

  const collectionHandle = useMemo(() => {
    // AU/NZ specific logic
    if (AU_NZ_STORES.includes(store)) {
      // Use v53 collection for AU/NZ customers shipping to US/CA
      if (isUSCAShipping) return MEMBER_PORTAL_COLLECTION_V53;
      // Use standard AU/NZ collection
      return ANZ_COLLECTION;
    }
    // APAC (ROA) specific logic
    if (locale === LocaleEnum.apacUS) {
      // Use v53 collection for APAC (ROA) customers shipping to US/CA
      if (isUSCAShipping) return MEMBER_PORTAL_COLLECTION_V53;
      // Use standard APAC collection
      return MEMBER_PORTAL_APAC_COLLECTION;
    }

    if ([StoreEnum.eu, StoreEnum.eud].includes(store)) {
      // Select based on active subscription view
      if (isSubPage) {
        return isDACHShipping ? EU_DE_COLLECTION : EU_EN_COLLECTION;
      }

      // Select based on active locale
      return DE_LOCALES.includes(locale) ? EU_DE_COLLECTION : EU_EN_COLLECTION;
    }

    if ([StoreEnum.uk, StoreEnum.ukd].includes(store)) {
      return MEMBER_PORTAL_COLLECTION_V53;
    }

    return v53Enabled ? MEMBER_PORTAL_COLLECTION_V53 : MEMBER_PORTAL_COLLECTION;
  }, [store, locale, v53Enabled, isSubPage, isUSCAShipping, isDACHShipping]);

  return { collectionHandle };
}
